import { clsx } from 'clsx';
import { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n';
import { ChevronIcon, CrossIcon } from '../icons';
import { closePhotos, currentPhotos, stepPhoto, subscribePhotos } from '../lib/photos';
import styles from './ui.module.css';

/**
 * Свой просмотр фотографий на весь экран — для веб-версии ВК, где нативная
 * галерея наши снимки не покажет (почему — в `shared/lib/photos.ts`).
 *
 * Ставится ОДИН раз в каркасе приложения, а не рядом с каждым кадром: портал
 * из кнопки-миниатюры выносит разметку в `body`, но события всё равно всплыли
 * бы по дереву React обратно в ту кнопку, и нажатие «закрыть» открывало бы
 * просмотр заново.
 *
 * Рисуется порталом в `document.body` по той же причине, что и `Dialog`:
 * внутри трансформированной панели `position: fixed` считается от панели.
 *
 * Фон — НАСТОЯЩАЯ кнопка, а не `div` с `onClick`: нажатие мимо снимка это
 * действие, и достаётся оно вместе с клавиатурой и скринридером. Заодно
 * снимку и стрелкам не нужен `stopPropagation` — они фону не потомки.
 */
export function PhotoViewer() {
  const view = useSyncExternalStore(subscribePhotos, currentPhotos);

  useEffect(() => {
    if (view === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePhotos();
      if (event.key === 'ArrowLeft') stepPhoto(-1);
      if (event.key === 'ArrowRight') stepPhoto(1);
    };
    document.addEventListener('keydown', onKeyDown);

    // Лента под просмотром не должна ехать от колеса мыши: в вебе это
    // единственный способ прокрутки, и уехавший экран потом не вернуть —
    // просмотр закрывается не туда, откуда открылся.
    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = scroll;
    };
  }, [view]);

  if (view === null) return null;

  const many = view.images.length > 1;

  return createPortal(
    <dialog open aria-modal="true" aria-label={t('ui.photo.title')} className={styles.photoOverlay}>
      <button
        type="button"
        className={clsx(styles.reset, styles.photoBackdrop)}
        aria-label={t('ui.photo.close')}
        onClick={closePhotos}
      />

      <img className={styles.photoImage} src={view.images[view.index]} alt="" />

      <button
        type="button"
        className={clsx(styles.reset, styles.photoButton, styles.photoClose)}
        aria-label={t('ui.photo.close')}
        onClick={closePhotos}
      >
        <CrossIcon size={16} />
      </button>

      {many ? (
        <>
          <button
            type="button"
            className={clsx(styles.reset, styles.photoButton, styles.photoPrev)}
            aria-label={t('ui.photo.prev')}
            onClick={() => stepPhoto(-1)}
          >
            <ChevronIcon direction="left" size={16} />
          </button>
          <button
            type="button"
            className={clsx(styles.reset, styles.photoButton, styles.photoNext)}
            aria-label={t('ui.photo.next')}
            onClick={() => stepPhoto(1)}
          >
            <ChevronIcon size={16} />
          </button>
          <span className={styles.photoCount}>
            {t('ui.photo.count', { current: view.index + 1, total: view.images.length })}
          </span>
        </>
      ) : null}
    </dialog>,
    document.body,
  );
}
